import * as THREE from "three";
import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js';
import { ConvexGeometry } from 'three/addons/geometries/ConvexGeometry.js';
import { Text } from '@react-three/drei';
import { Point, Points  } from "../shape/Point";
import { TubeCurve } from '../shape/Spline';
import React, { memo, useState, useEffect, useRef } from 'react';
import { drawIntersectionPoints,getContours } from './utils';
import { SUBTRACTION, Brush, Evaluator } from 'three-bvh-csg';

import { 
  calcCentroid,
  calcCirclePoints, 
  calculateCurveCenter, 
  calculateCurveLength, 
  getContourPointsCircumAll,
  offsetCurve, 
  intersectionCurvePlane,
  getBottomPoints,
  getBottom2HolePoints,
  getBottomCubePoints,
  calculateIntersectionPoints,
  getWrapPoints,
  lineSegmentsIntersect,
  findClosestToZeroY,
  calcIntersectionMedialCurves,
  findClosestPointOnPoints,
  remapCurve,
  alignSeamPoints,
  alignSeamPointsFrom3Dto2D,
  makeCurveClockwise,
  sortPoints,
  createLoftSurfaceFrom2Curves,
  createLoftSurfaceFromCurves,
  calculateTweenCurve,
  smoothGeom
} from '../shape/utils';


/**
 * Returns an array of open edge points for a given mesh socket.
 * @param {THREE.Mesh} meshSocket - The mesh socket to find open edge points for.
 * @returns {Array<THREE.Vector3>} An array of open edge points.
 */
const getOpenEdgePoints = (meshSocket) => {
  /**
   * Finds open edges in a given geometry.
   * @param {THREE.BufferGeometry} geometry - The geometry to search for open edges.
   * @param {number} [epsilon=1e-4] - The tolerance value for comparing floating point numbers.
   * @returns {Array<Array<number>>} An array of open edges, where each edge is represented by an array of two vertex indices.
   */
  const findOpenEdges = (geometry, epsilon = 1e-4) => {
    const edges = new Map();
    const faceIndices = geometry.index.array;
  
    for (let i = 0; i < faceIndices.length; i += 3) {
      for (let j = 0; j < 3; j++) {
        const a = faceIndices[i + j];
        const b = faceIndices[i + ((j + 1) % 3)];
        const key = `${Math.min(a, b)}_${Math.max(a, b)}`;
  
        if (edges.has(key)) {
          edges.set(key, edges.get(key) + 1);
        } else {
          edges.set(key, 1);
        }
      }
    }
  
    const openEdges = [];
    edges.forEach((count, edge) => {
      if (count === 1) {
        const vertices = edge.split('_').map(a => parseInt(a));
        openEdges.push(vertices);
      }
    });
  
    return openEdges;
  }
  
  const openEdges = findOpenEdges(meshSocket.geometry);
  const firstIdx = openEdges[0][0];
  const lastIdx = openEdges[openEdges.length-1][1];
  // connect open edges
  const openEdgePoints = [];
  for (let i = firstIdx; i < lastIdx+1; i++) {
    openEdgePoints.push(new THREE.Vector3(
      meshSocket.geometry.attributes.position.array[i*3],
      meshSocket.geometry.attributes.position.array[i*3+1],
      meshSocket.geometry.attributes.position.array[i*3+2]
    ));
  }
  openEdgePoints.push(openEdgePoints[0].clone()); // close the loop

  return openEdgePoints;
}


/**
 * Returns an array of weft points from an array of warp points.
 * @param {Array<Array<number>>} warpPointsAll - An array of arrays containing the x and y coordinates of the warp points.
 * @param {number} div - The number of divisions to create between the warp points.
 * @returns {Array<Array<<number>>} An array of arrays containing the x and y coordinates of the weft points.
 */
const getWeftPointsFromWarpPoints = (warpPointsAll, div) => {
  const weftPointsAll = [];

  for (let i = 0; i < div+1; i++) {
    const weftPoints = [];
    warpPointsAll.map((warpPoints) => {
      weftPoints.push(warpPoints[i]);
    });
    weftPoints.push(weftPoints[0]); // close curve
    weftPointsAll.push(weftPoints);
  }
  return weftPointsAll;
};


/**
   * Returns an array of projected bottom points to meshSocketOffset(Outer surface) with raycaster.
   * @param {Array<THREE.Vector3>} points - An array of THREE.Vector3 points to project onto the bufferGeometry.
   * @param {THREE.BufferGeometry} bufferGeometry - The bufferGeometry to project the points onto.
   * @returns {Array<THREE.Vector3>} An array of projected bottom points.
   */
const getProjectedPoints2BufferGeometry = (points, bufferGeometry) => {
  // get projected bottom points to meshSocketOffset(Outer surface) with raycaster
  const pointsProjected = points.map((point) => {
    const raycaster = new THREE.Raycaster(point, new THREE.Vector3(0, 1, 0));
    const intersects = raycaster.intersectObject(bufferGeometry);
    // sort intersects by y value and get maximum y point
    intersects.sort((a, b) => {
      return b.point.y - a.point.y;
    });
    return intersects[0].point;
  })
  
  return pointsProjected;
}




/**
 * Trim mesh with 3D projected trim points
 */
export const MThick = memo(({
  oriMeshSocket,
  upperEdgePoints,
  lowerEdgePoints,
  thickness,
  step,
  flexionAngle,
  connector,
  apConnector,
  mlConnector,
  boundaryCurveIndex,
  callback0,
  callback1,
  callback2,
  color,
  colorHigh,
  colorSub
}) => {
  const objects = [];
  const objectsSub = [];
  var key = 0;
  const div = 50;
  const angleStep = 10;
  const segmentsWidth = div;
  const segmentsHeight = div;
  
  const fourHoleDepth = 100;
  const radiusInnerFourHole = 3.2;
  const radiusOuterFourHole = 7.6;
  const radiusCounterHole = 18;
  const offsetOuterFourHole = 5;
  const counterHoleDepth = 10;
  const offsetCounterHole = 3;
  const offsetBoolean = 0.5; // this is for escape from the error of boolean operation
  const intervalFourHole = 18;
  const substractions = [];

  const material = new THREE.MeshLambertMaterial( { color: color, side: THREE.DoubleSide, transparent: true, opacity: 0.5} )
  const materialHigh = new THREE.MeshLambertMaterial( { color: colorHigh, side: THREE.DoubleSide, transparent: true, opacity: 0.5} )

  console.log("MThick Local");

  // initialize objectsSub
  objectsSub.length = 0;

  //useEffect(() => {
  const meshSocket = oriMeshSocket.clone();
  meshSocket.geometry = oriMeshSocket.geometry.clone(); // for deepcopy
  meshSocket.geometry.rotateX(flexionAngle / 180 * Math.PI);
  
  // apply flexion angle to edge points
  upperEdgePoints = upperEdgePoints.map((point) => {
    return point.clone().applyAxisAngle(new THREE.Vector3(1,0,0), flexionAngle / 180 * Math.PI);
  });
  lowerEdgePoints = lowerEdgePoints.map((point) => {
    return point.clone().applyAxisAngle(new THREE.Vector3(1,0,0), flexionAngle / 180 * Math.PI);
  });
  //meshSocket.geometry.computeVertexNormals();


  const meshSocketOffset = meshSocket.clone();
  meshSocketOffset.geometry = meshSocket.geometry.clone(); // for deepcopy
  meshSocketOffset.geometry.computeBoundingBox();
  const yMin = meshSocketOffset.geometry.boundingBox.min.y;
  
  // offset mesh vertices as normal directiond
  for (let i = 0; i < meshSocketOffset.geometry.attributes.position.array.length; i+=3) {
    // move point
    meshSocketOffset.geometry.attributes.position.array[i] -= meshSocketOffset.geometry.attributes.normal.array[i] * thickness;
    meshSocketOffset.geometry.attributes.position.array[i+1] -= meshSocketOffset.geometry.attributes.normal.array[i+1] * thickness;
    meshSocketOffset.geometry.attributes.position.array[i+2] -= meshSocketOffset.geometry.attributes.normal.array[i+2] * thickness;
  }

  // const openEdgePoints = getOpenEdgePoints(meshSocketOffset);
  const bottomCenter = calcCentroid(lowerEdgePoints);
  
  // get warp points of mesh socket
  var offsetY = 0
  if (connector == 0) { // cube joint
    offsetY = 0;
  } else if (connector == 1) { // two hole joint
    offsetY = 15;
  } else if (connector == 2) { // four hole joint
    offsetY = 3;
  }
  //const warpPointsAll = getContourPointsCircumAll(meshSocketOffset, bottomCenter, 0, 0, angleStep, yMin+offsetY);
  let [warpPointsAll, refPoints] = getWrapPoints(meshSocketOffset, bottomCenter, 0, 0, angleStep, yMin+offsetY, false);
  // smooth warp points
  warpPointsAll = warpPointsAll.map((warpPoints) => {
    const nPoint = Math.floor(warpPoints.length*0.2);
    // const nPoint = Math.floor(10);
    return new THREE.CatmullRomCurve3(warpPoints, false).getSpacedPoints(nPoint);
  });
  
  // show warp points
  warpPointsAll.map((warpPoints, i) => {
    if (i !== 100) {
      objectsSub.push(<TubeCurve
        key={key}
        vpoints={warpPoints}
        radius={0.5}
        color={colorHigh}
      />
      );
      key += 1;

      //objectsSub.push(<Points key={key} vpoints={warpPoints} color={colorSub} size={1} />);
    }
  });
  
  var offsetLowerEdgePoints = lowerEdgePoints.map((point) => {
    //const idx = meshSocket.geometry.attributes.position.array.indexOf(point.x);
    var idx = -9999;
    const tolerance = 2;
    for (let i = 0; i < meshSocket.geometry.attributes.position.array.length; i+=3) {
      if (
        (Math.abs(meshSocket.geometry.attributes.position.array[i] - point.x) < tolerance) &&
        (Math.abs(meshSocket.geometry.attributes.position.array[i+1] - point.y) < tolerance) &&
        (Math.abs(meshSocket.geometry.attributes.position.array[i+2] - point.z) < tolerance)
        )
        {
          idx = i;
        }
    }
    
    const thicknessUpperEdge = new THREE.Vector3(
      meshSocket.geometry.attributes.normal.array[idx],
      meshSocket.geometry.attributes.normal.array[idx+1],
      meshSocket.geometry.attributes.normal.array[idx+2]
    ).multiplyScalar(-thickness);

    return point.clone().add(thicknessUpperEdge);
  });

  const oriN = offsetLowerEdgePoints.length;
  offsetLowerEdgePoints = new THREE.CatmullRomCurve3(offsetLowerEdgePoints, false).getSpacedPoints(10);
  offsetLowerEdgePoints = new THREE.CatmullRomCurve3(offsetLowerEdgePoints, true).getSpacedPoints(oriN-1);


  // sort warp points
  const warpPointsBottom = [];
  warpPointsAll.map((warpPoints, i) => {
    //warpPointsBottom.push(warpPoints[0]);
    // get minimum y point
    let minPoint = warpPoints[0];
    warpPoints.map((point) => {
      if (point.y < minPoint.y) minPoint = point;
    });
    warpPointsBottom.push(minPoint);
  });

  // sort warp points
  const warpPointsAllSorted = [];
  var tempIdx = 0
  warpPointsAllSorted.push(warpPointsAll[0]);
  for (let i = 1; i < warpPointsAll.length; i++) {
    const refPointBase = warpPointsBottom[tempIdx];
    const refPointNext = warpPointsBottom[i];
    const idxNextOpposite = (i+warpPointsAll.length/2) % warpPointsAll.length;
    const refPointNextOpposite = warpPointsBottom[idxNextOpposite];

    const distNext = refPointBase.distanceTo(refPointNext);
    const distNextOpposite = refPointBase.distanceTo(refPointNextOpposite);
    
    if (distNext < distNextOpposite) {
      warpPointsAllSorted.push(warpPointsAll[i]);
      tempIdx = i;
    } else {
      warpPointsAllSorted.push(warpPointsAll[idxNextOpposite]);
      tempIdx = idxNextOpposite;
    }
  }

  var warpPointsTrimAll = [];
  for (let i = 0; i < warpPointsAllSorted.length; i++) {
    const points = new THREE.CatmullRomCurve3(warpPointsAllSorted[i], false).getSpacedPoints(div);
    warpPointsTrimAll.push(points);
  }

  // get weft points
  var weftPointsAll = getWeftPointsFromWarpPoints(warpPointsTrimAll, div);

  const linearWeftPoints = [];
  const linearWarpPoints = [];
  // Tween weft points and bottom rectangle points to have necessary thickness
  if ( connector == 2 ) {
    // get projection points of bottom points to mesh socket.
    let bottomPointsForProjection = getBottomPoints(new THREE.Vector3(-mlConnector,0,apConnector).add(new THREE.Vector3(0, -1000, 0)));
    // get spaced points
    bottomPointsForProjection = new THREE.CatmullRomCurve3(bottomPointsForProjection, false).getSpacedPoints(20);
    const projectedBottomPoints = getProjectedPoints2BufferGeometry(bottomPointsForProjection, meshSocketOffset);
    const projectedBottomPointsY = projectedBottomPoints.map((point) => {
      return point.y;
    });
    const maxProjectedBottomPointsY = Math.max(...projectedBottomPointsY);

    // TODO: this is overlap definition with the bottom curve definition on the below code, so need to merge it later.
    const bottomSocketY = meshSocketOffset.geometry.boundingBox.min.y;
    bottomOffset = -20; //16
    const bottomSurfaceCenter = new THREE.Vector3(-mlConnector, bottomSocketY+bottomOffset, apConnector);
    bottomPoints = getBottomPoints(bottomSurfaceCenter);

    // make linear curve points
    //// get lastIdx
    let lastIdx = null;
    weftPointsAll.map((weftPoints, idx) => {
      // get center point of weft points
      const centerPoint = calcCentroid(weftPoints);
      if ( centerPoint.y < maxProjectedBottomPointsY ) {
        lastIdx = idx;
      }
    });

    //lastIdx = 15; // TODO: this is temporary code for debug
    callback1(lastIdx);

    const linearCurves = [];
    const linearStartPoints = calculateIntersectionPoints(new THREE.CatmullRomCurve3(bottomPoints, true), angleStep, close=true);
    const linearEndPoints = calculateIntersectionPoints(new THREE.CatmullRomCurve3(weftPointsAll[boundaryCurveIndex], true), angleStep, close=true);

    for (let i = 0; i < linearEndPoints.length; i++) {
      const temp = [];
      linearCurves.push(new THREE.LineCurve3(linearStartPoints[i], linearEndPoints[i]));
      const ratio = bottomOffset / (calcCentroid(linearStartPoints).y - calcCentroid(linearEndPoints).y);
      for (let j=0; j<boundaryCurveIndex; j++) {
        temp.push(linearCurves[i].getPoint(ratio + (1-ratio)*j/boundaryCurveIndex))
      }
      linearWarpPoints.push(temp);
    }

    for (let i = 0; i < boundaryCurveIndex; i++) {
      const temp = linearWarpPoints.map((points) => points[i]);
      linearWeftPoints.push(temp);
    }

    // make patch curve with bottom curve points and weft points
    weftPointsAll = weftPointsAll.map((weftPoints, i) => {
      // get center point of weft points
      const centerPoint = calcCentroid(weftPoints);
      
      // patch weft points and bottom curvepoints
      if ( centerPoint.y < maxProjectedBottomPointsY ) {
        const patchPoints0 = calculateIntersectionPoints(new THREE.CatmullRomCurve3(weftPoints, true), angleStep, close=true); // cross section points
        const patchPoints1 = calculateIntersectionPoints(new THREE.CatmullRomCurve3(bottomPoints, true), angleStep, close=true); // bottom points
        // replace y value of patchPoints to original weftPoints y value
        patchPoints1.map((point, j) => {
          point.y = patchPoints0[j].y;
        });
        var patchPoints2 = null; // linear points
        if ( i < boundaryCurveIndex ) {
          patchPoints2 = calculateIntersectionPoints(new THREE.CatmullRomCurve3(linearWeftPoints[i], true), angleStep, close=true);
          patchPoints2.map((point, j) => {
            point.y = patchPoints0[j].y;
          });
        }
        
        var patchPoints = [];

        // cacl distance from center point to patch points, to determine which points are more far from center point
        for (let j=0; j<patchPoints0.length; j++) {
          const dist0 = patchPoints0[j].distanceTo(centerPoint);
          const dist1 = patchPoints1[j].distanceTo(centerPoint);
          const dist = [dist0, dist1];
          if ( i < boundaryCurveIndex ) {
            const dist2 = patchPoints2[j].distanceTo(centerPoint);
            dist.push(dist2);
          }
          
          // if dist0 is more far from center point, add patchPoints0[i] to patchPoints
          if (dist.indexOf(Math.max(...dist)) == 0) {
            patchPoints.push(patchPoints0[j]);
          } else if (dist.indexOf(Math.max(...dist)) == 1) {
            patchPoints.push(patchPoints1[j]);
          } else if (dist.indexOf(Math.max(...dist)) == 2) {
            patchPoints.push(patchPoints2[j]);
          }
        }

        // rebuild patchPoints to smooth
        const n = patchPoints.length;
        patchPoints = new THREE.CatmullRomCurve3(patchPoints, false).getSpacedPoints(Math.floor(n*0.8));
        patchPoints = new THREE.CatmullRomCurve3(patchPoints, true).getSpacedPoints(n);

        return patchPoints;
      } else {
        return weftPoints;
      }
    });
  }
  


  // bottom curve ///////////////////////////////////////////////
  const bottomSocketY = meshSocketOffset.geometry.boundingBox.min.y;
  var bottomOffset = 0
  var bottomPoints = [];
  var bottomPointsSubstruct = []
  if ( connector == 0 ) { // cube joint
    const bottomOffset = -25; //16
    const bottomSurfaceCenter = new THREE.Vector3(0, bottomSocketY+bottomOffset, 0);
    bottomPointsSubstruct  = getBottomCubePoints(bottomSurfaceCenter); // cube shape

    // patch socket bottom curve and cube curve

    weftPointsAll.unshift(offsetLowerEdgePoints); // add bottom points to warp points
  } else if ( connector == 1 ) { // two hole joint
    bottomOffset = -20; //16
    const bottomSurfaceCenter = new THREE.Vector3(0, bottomSocketY+bottomOffset, 0);
    const points = getBottom2HolePoints(bottomSurfaceCenter); // two hole
    bottomPoints.push(points); // two hole
    bottomPoints.push(points.map((point) => {
      return new THREE.Vector3(point.x, point.y+10, point.z);
    })); // two hole
    
  } else if ( connector == 2 ) { // four hole joint
    bottomOffset = -20; //16
    const bottomSurfaceCenter = new THREE.Vector3(-mlConnector, bottomSocketY+bottomOffset, apConnector);
    bottomPoints.push(getBottomPoints(bottomSurfaceCenter)); // four hole
  }

  // add bottom points to warp points
  for (let i = bottomPoints.length-1; i >= 0; i--) {
    const bottomIntersectionPoints = calculateIntersectionPoints(new THREE.CatmullRomCurve3(bottomPoints[i]), angleStep, close=true);
    // add bottom points to warp points
    weftPointsAll.unshift(bottomIntersectionPoints);
  }

  // reverse weft points to flip normal
  weftPointsAll.map((weftPoints) => {
    weftPoints.reverse();
  });


  // align seam points
  for ( let i = 0; i < weftPointsAll.length-1; i++ ) {
    if ( i > 0 ) {
      weftPointsAll[i] = alignSeamPointsFrom3Dto2D(weftPointsAll[i-1], weftPointsAll[i]);
    }
  }


  // linearWeftPoints.map((points) => {
  //   objectsSub.push(<TubeCurve 
  //     key={key} 
  //     vpoints={points} 
  //     radius={0.5}
  //     color={colorSub}
  //     />
  //   );
  //   key += 1;
  // });

  // warpPointsAll.map((warpPoints, i) => {
  //   objectsSub.push(<TubeCurve 
  //     key={key} 
  //     vpoints={warpPoints} 
  //     radius={0.5}
  //     color={colorHigh}
  //     />
  //   );
  //   key += 1;
  // });

  
  // display weft points and highlight boundary curve.
  var colorWeftCurve = null;
  weftPointsAll.map((weftPoints, i) => {
    (i==boundaryCurveIndex) ? colorWeftCurve = colorSub : colorWeftCurve = colorHigh;

    const n = weftPoints.length;
    const displayPoints = new THREE.CatmullRomCurve3(weftPoints, true).getSpacedPoints(n);

    objectsSub.push(<TubeCurve 
      key={key} 
      vpoints={displayPoints} 
      radius={0.5}
      color={colorWeftCurve}
      />
    );
    key += 1;
  });

  // // display first point of weft points
  // weftPointsAll.map((weftPoints, i) => {
  //   objectsSub.push(<Point
  //     key={key}
  //     vpoint={weftPoints[0]}
  //     color={colorHigh}
  //     size={2}
  //     />
  //   );
  //   key += 1;
  // });

  // // display first point of weft points
  // weftPointsAll.map((weftPoints, i) => {
  //   objectsSub.push(<Point
  //     key={key}
  //     vpoint={weftPoints[10]}
  //     color={colorHigh}
  //     size={2}
  //     />
  //   );
  //   key += 1;
  // });
    

  var outerLoftSurface = null;
  if (step == 1) {
    // loft outer surface ///////////////////////////////////////////////
    outerLoftSurface = createLoftSurfaceFromCurves(weftPointsAll, segmentsWidth, segmentsHeight, color, true);
    objects.push(<primitive key={key} object={outerLoftSurface} />);
    key += 1;

    // get lower and upper edge vertices
    var loftPosition = outerLoftSurface.geometry.attributes.position.array;

    const outerUpperEdgePoints = [];
    const outerLowerEdgePoints = [];
    for (let j = 0; j < loftPosition.length; j+=3) {
      if (j < 3*(segmentsWidth+1)) {
        outerLowerEdgePoints.push(new THREE.Vector3(loftPosition[j], loftPosition[j+1], loftPosition[j+2]));
      } else if (j >= 3*(segmentsWidth+1)*(segmentsHeight)) {
        outerUpperEdgePoints.push(new THREE.Vector3(loftPosition[j], loftPosition[j+1], loftPosition[j+2]));
      }
    }
    callback2(outerUpperEdgePoints);
    ////////////////////////////////////////////////////////////////

    // bottom cap for outer surface ////////////////////////////////
    const centerBottom = calcCentroid(outerLowerEdgePoints);
    const oldVertices = outerLoftSurface.geometry.attributes.position.array;
    const newVertices = [...oldVertices, centerBottom.x, centerBottom.y, centerBottom.z];

    // get original faces and update it
    const oldFaces = outerLoftSurface.geometry.index.array;
    var newFaces = [...oldFaces];

    const lastVertexIndex = newVertices.length/3-1;

    // add faces for bottom cap
    for (let i = 0; i < segmentsWidth; i++) {
      newFaces.push(i+1, i, lastVertexIndex);
    }
    //////////////////////////////////////////////////////////







    // update attributes
    const newAttribute = new THREE.Float32BufferAttribute(newVertices, 3);
    outerLoftSurface.geometry.setAttribute('position', newAttribute);
    outerLoftSurface.geometry.setIndex(newFaces);
    outerLoftSurface.geometry.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(newVertices.length), 3)); // need to add this line to update normals if you add vertices
    //outerLoftSurface.geometry = BufferGeometryUtils.mergeVertices(outerLoftSurface.geometry, 2);
    outerLoftSurface.geometry.attributes.normal.needsUpdate = true;
    outerLoftSurface.geometry.attributes.position.needsUpdate = true;
    outerLoftSurface.geometry.index.needsUpdate = true;
    outerLoftSurface.geometry.computeVertexNormals();
    //////////////////////////////////////////////////////////

    // top surface between inner and outer surface ///////////////////////////////////////////////
    var divTopWeftPointsAll = [];
    var stackTop = 2;
    //divTopWeftPointsAll.push(divInnerWeftPointsAll[divInnerWeftPointsAll.length-1]);
    divTopWeftPointsAll.push(upperEdgePoints);
    divTopWeftPointsAll.push(outerUpperEdgePoints);

    divTopWeftPointsAll = divTopWeftPointsAll.map((points) => {
      return makeCurveClockwise(points);
    });

    var loftPoints0 = divTopWeftPointsAll[0];
    var loftPoints1 = alignSeamPoints(loftPoints0, divTopWeftPointsAll[1]);
    //var loftPoints1 = divTopWeftPointsAll[1];

    var loftCurve0 = new THREE.CatmullRomCurve3(loftPoints0, false);
    var loftCurve1 = new THREE.CatmullRomCurve3(loftPoints1, false);

    var loftSurface = createLoftSurfaceFrom2Curves(loftCurve0, loftCurve1, segmentsWidth, stackTop, color);

    objects.push(
      <primitive key={key} object={loftSurface} />
    );
    key += 1;

  }

  var counterHoleGeometry = null;

  if (step == 1){ // Apply thickeness
    if ( connector == 0) { // cube joint
    // 頂点座標の定義
      const width = 25/2;
      const height = bottomPointsSubstruct[0][0].y;
      let vertices = new Float32Array([
        -width, height, -width,
        width, height, -width,
        width,  height, width,
        width,  height, width,
        -width,  height, width,
        -width, height, -width
      ]);
      
      const vertices4Points = [
        new THREE.Vector3(-width, height, -width),
        new THREE.Vector3(width, height, -width),
        new THREE.Vector3(width,  height, width),
        new THREE.Vector3(-width,  height, width),
      ]

      // BufferGeometryの作成
      let geometry = new THREE.BufferGeometry();

      // 頂点座標データをgeometryにセット
      geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));

      const meshPoints = new THREE.Mesh(geometry, materialHigh);
      //objectsSub.push(<primitive key={key} object={meshPoints} />);
      
      let raycaster = new THREE.Raycaster();
      let direction = new THREE.Vector3(0, -1, 0); // ray direction

      const socketPoints = outerLoftSurface.geometry.attributes.position.array;
      const intersectIdx = [];
      var intersectPoints = [];
      for (let i = 0; i < outerLoftSurface.geometry.attributes.position.array.length; i+=3) {
        const point = new THREE.Vector3(socketPoints[i], socketPoints[i+1], socketPoints[i+2]);
        //raycaster.set(point, direction);
        //let intersects = raycaster.intersectObject(meshPoints);
        // if (intersects.length % 2 == 1) {
        //   // move point
        //   meshSocket.geometry.attributes.position.array[i+1] -= 10
        // }
        if (Math.abs(point.x) <= width && Math.abs(point.z) <= width && point.y <= 0) {
          intersectIdx.push(i);
          intersectPoints.push(point);

          if (i == outerLoftSurface.geometry.attributes.position.array.length-3) {
          } else {

            const offset = height - point.y;

            const tempPoint = new THREE.Vector3(
              point.x,
              point.y + offset,
              point.z
            )
            const closestPoint = findClosestPointOnPoints(vertices4Points, tempPoint);
            
            outerLoftSurface.geometry.attributes.position.array[i] = closestPoint.point.x
            outerLoftSurface.geometry.attributes.position.array[i+2] = closestPoint.point.z

            outerLoftSurface.geometry.attributes.position.array[i+1] = height
          }
        }
      }
    } else if (connector==1 || connector==2) { // two or four hole
      // counter for female pyramid adapter /////////////////////////

      const bottomOffset = -20; //16

      counterHoleGeometry = new THREE.CylinderGeometry(radiusCounterHole, radiusCounterHole, counterHoleDepth, 32);
      counterHoleGeometry.translate(0, bottomSocketY+bottomOffset-counterHoleDepth/2+offsetCounterHole, 0);
      const counterHoleMesh = new THREE.Mesh( counterHoleGeometry, material ) ;

      objectsSub.push(
        <primitive key={key} object={counterHoleMesh} />
      );
      key += 1;

      if (connector==2) substractions.push(counterHoleGeometry)
      //////////////////////////////////////////////////////////////////////

      // 4 hole inner ///////////////////////////////////////////////
      var center = new THREE.Vector3(0, bottomSocketY+bottomOffset-offsetBoolean, 0);

      const fourHoleGeometry0 = new THREE.CylinderGeometry(radiusInnerFourHole, radiusInnerFourHole, fourHoleDepth, 32);
      const fourHoleGeometry1 = fourHoleGeometry0.clone();
      const fourHoleGeometry2 = fourHoleGeometry0.clone();
      const fourHoleGeometry3 = fourHoleGeometry0.clone();
      fourHoleGeometry0.translate(center.x+intervalFourHole, center.y, center.z+intervalFourHole);
      fourHoleGeometry1.translate(center.x+intervalFourHole, center.y, center.z-intervalFourHole);
      fourHoleGeometry2.translate(center.x-intervalFourHole, center.y, center.z+intervalFourHole);
      fourHoleGeometry3.translate(center.x-intervalFourHole, center.y, center.z-intervalFourHole);

      const fourHoleMesh0 = new THREE.Mesh( fourHoleGeometry0, material );
      const fourHoleMesh1 = new THREE.Mesh( fourHoleGeometry1, material ) ;
      const fourHoleMesh2 = new THREE.Mesh( fourHoleGeometry2, material ) ;
      const fourHoleMesh3 = new THREE.Mesh( fourHoleGeometry3, material ) ;
      objectsSub.push(
        <primitive key={key} object={fourHoleMesh0} />
      );
      key += 1;
      objectsSub.push(
        <primitive key={key} object={fourHoleMesh1} />
      );
      key += 1;
      objectsSub.push(
        <primitive key={key} object={fourHoleMesh2} />
      );
      key += 1;
      objectsSub.push(
        <primitive key={key} object={fourHoleMesh3} />
      );
      key += 1;
      
      
      if (connector==2) substractions.push(fourHoleGeometry0)
      substractions.push(fourHoleGeometry1)
      if (connector==2) substractions.push(fourHoleGeometry2)
      substractions.push(fourHoleGeometry3)
      /////////////////////////////////////////////////////////

      // 4 hole outer ///////////////////////////////////////////////
      var center = new THREE.Vector3(0, bottomSocketY+bottomOffset+offsetOuterFourHole+fourHoleDepth/2, 0);

      const outerFourHoleGeometry0 = new THREE.CylinderGeometry(radiusOuterFourHole, radiusOuterFourHole, fourHoleDepth, 32);
      const outerFourHoleGeometry1 = outerFourHoleGeometry0.clone();
      const outerFourHoleGeometry2 = outerFourHoleGeometry0.clone();
      const outerFourHoleGeometry3 = outerFourHoleGeometry0.clone();
      outerFourHoleGeometry0.translate(center.x+intervalFourHole, center.y, center.z+intervalFourHole);
      outerFourHoleGeometry1.translate(center.x+intervalFourHole, center.y, center.z-intervalFourHole);
      outerFourHoleGeometry2.translate(center.x-intervalFourHole, center.y, center.z+intervalFourHole);
      outerFourHoleGeometry3.translate(center.x-intervalFourHole, center.y, center.z-intervalFourHole);

      const outerFourHoleMesh0 = new THREE.Mesh( outerFourHoleGeometry0, material );
      const outerFourHoleMesh1 = new THREE.Mesh( outerFourHoleGeometry1, material ) ;
      const outerFourHoleMesh2 = new THREE.Mesh( outerFourHoleGeometry2, material ) ;
      const outerFourHoleMesh3 = new THREE.Mesh( outerFourHoleGeometry3, material ) ;
      objectsSub.push(
        <primitive key={key} object={outerFourHoleMesh0} />
      );
      key += 1;
      objectsSub.push(
        <primitive key={key} object={outerFourHoleMesh1} />
      );
      key += 1;
      objectsSub.push(
        <primitive key={key} object={outerFourHoleMesh2} />
      );
      key += 1;
      objectsSub.push(
        <primitive key={key} object={outerFourHoleMesh3} />
      );
      key += 1;

      if (connector==2) substractions.push(outerFourHoleGeometry0)
      substractions.push(outerFourHoleGeometry1)
      if (connector==2) substractions.push(outerFourHoleGeometry2)
      substractions.push(outerFourHoleGeometry3)
    }
  }


  var geomOutput = null;
  // combine all objects to one mesh ///////////////////////////////////////////////
  if (step == 0) {
    geomOutput = meshSocket.geometry;
  } else if (step==1) {
    // if (connector == 0) { // cube
    //   const csgEvaluator = new Evaluator();
    //   const substractions = [];
    //   const cubeGeom = new THREE.BoxGeometry( 25, 25, 500 );
    //   cubeGeom.rotateX(Math.PI/2);
    //   cubeGeom.translate(0, -250, 0); 
    //   substractions.push(cubeGeom);

    //   const brush0 = new Brush( outerLoftSurface.geometry );
    //   const brush1 = new Brush( cubeGeom );
    //   const result = csgEvaluator.evaluate( brush0, brush1, SUBTRACTION );
    //   outerLoftSurface.geometry = result.geometry;
    // }

    geomOutput = BufferGeometryUtils.mergeBufferGeometries([
      meshSocket.geometry,
      outerLoftSurface.geometry,
      loftSurface.geometry,
    ]);
    geomOutput = BufferGeometryUtils.mergeVertices(geomOutput, 2);
    if (connector !== 0) geomOutput = smoothGeom(geomOutput, 2);
  }

  if (connector == 1 || connector == 2) {
    const csgEvaluator = new Evaluator();
    
    for (let i = 0; i < substractions.length; i++) {
      const brush0 = new Brush( geomOutput );
      const brush1 = new Brush( substractions[i] );
      const result = csgEvaluator.evaluate( brush0, brush1, SUBTRACTION );
      geomOutput = result.geometry;
    }
  }

  if (geomOutput) {
    const meshOutput = new THREE.Mesh(geomOutput, material);
    callback0(meshOutput);
  }

  //   setObjectsSub(objectsSub);
  // }, [
  //   oriMeshSocket,
  //   upperEdgePoints,
  //   lowerEdgePoints,
  //   thickness,
  //   step,
  //   flexionAngle,
  //   connector,
  //   apConnector,
  //   mlConnector,
  //   boundaryCurveIndex,
  // ]);


  //////////////////////////////////////////////////////////////////////////////////////


  // objectsSub.push(<TubeCurve
  //   key={key}
  //   //vpoints={upperEdgePoints}
  //   vpoints={openEdgePoints}
  //   radius={0.5}
  //   color={colorHigh}
  // />
  // );
  // key += 1;


  // objectsSub.push(<TubeCurve
  //   key={key}
  //   vpoints={intersectPoints}
  //   radius={0.5}
  //   color={colorHigh}
  // />
  // );
  // key += 1;

  return ( 
    (step == 0) ? (
      <group position={[0,0,0]}>
        {objectsSub}
      </group>
    ) : null
  )

  //return null;
});